import { BaseViewStore } from 'core/stores';
import { action, observable } from "mobx";
import { InventoryCalculationCutbackService } from "application/facility-order/services";
import _ from "lodash";
import { ModalParams } from 'core/models';

class NikoFacilityOrderNewFlowInventoryCalculationCutbackEditModalStore extends BaseViewStore {
    @observable accessor disabled = false;
    @observable accessor loadingData = false;
    @observable accessor adjustedQtySum = 0;
    @observable accessor inventoryCalculationCutbackItems;
    @observable accessor previousAdjustedQty = 0;
    @observable accessor isManualOverride = false;
    constructor(rootStore, componentProps) {
        super(rootStore);

        this.rootStore = rootStore;
        this.modalParams = componentProps.modalParams;
        this.facilityInventoryCalculationItem = this.modalParams.data.facilityInventoryCalculationItem;
        this.isCutback = this.modalParams.data.isCutback;
        this.disabled = this.modalParams.data.disabled;
        this.isDone = this.modalParams.data.isDone;

        this.service = new InventoryCalculationCutbackService(rootStore.application.baasic.apiClient);
        this.facilityInventoryCalculationItem = componentProps.modalParams.data.facilityInventoryCalculationItem;

        this.statusConfirmModal = new ModalParams({ onClose: null });
        this.statusConfirmModal.onConfirm = async () => {   
            await this.service.batchUpdate(this.inventoryCalculationCutbackItems);
            this.rootStore.notificationStore.success('LIST_LAYOUT.SUCCESS_BATCH_UPDATE');
            this.statusConfirmModal.close() 
            this.modalParams.close(); 
        };
        this.statusConfirmModal.onCancel = () => { this.statusConfirmModal.close() };
        this.statusConfirmModal.swapButtons = true;

        this.fetch([
            this.initialize()
        ]);
    }

    @action
    async initialize() {
        this.loadingData = true;
        await this.getInventoryCalculationCutback();
        this.loadingData = false;
    }

    @action.bound
    async getInventoryCalculationCutback() {
        const response = await this.service.find({
            sort: "PatientTemplateItem.PatientTemplate.ClinicalStatus.Name|asc",
            calculationItemId: this.facilityInventoryCalculationItem.id,
            embed: "PatientTemplateItem",
            rpp: 100
        });

        let responseData = _.extend(response.data, {
            item: _.map(response.data.item, (item => {
                return _.extend(
                    item, {
                    patient: item.patientTemplateItem && item.patientTemplateItem.patientTemplate && item.patientTemplateItem.patientTemplate.nikoPatientDetail && item.patientTemplateItem.patientTemplate.nikoPatientDetail.nikoPatient.fullName,
                    patientId: item.patientTemplateItem && item.patientTemplateItem.patientTemplate && item.patientTemplateItem.patientTemplate.nikoPatientDetail && item.patientTemplateItem.patientTemplate.nikoPatientDetail.id,
                    soQty: this.isDone ? item.initialOrderQuantity : item.patientTemplateItem && item.patientTemplateItem.salesOrderQuantity,
                    adjustedQty: item.adjustedQty,
                    difference: this.isDone ? item.initialOrderQuantity - item.adjustedQty : item.patientTemplateItem.salesOrderQuantity - item.adjustedQty,
                    eligibilityStatus: item.patientTemplateItem && item.patientTemplateItem.patientTemplate && item.patientTemplateItem.patientTemplate.nikoPatientDetail && item.patientTemplateItem.patientTemplate.nikoPatientDetail.eligibilityStatus && item.patientTemplateItem.patientTemplate.nikoPatientDetail.eligibilityStatus.name,
                    clinicalStatus: item.patientTemplateItem && item.patientTemplateItem.patientTemplate && item.patientTemplateItem.patientTemplate.clinicalStatus && item.patientTemplateItem.patientTemplate.clinicalStatus.name,
                    nonEditable: item.patientTemplateItem && 
                    item.patientTemplateItem.patientTemplate && 
                    (item.patientTemplateItem.patientTemplate.clinicalStatus === null ||
                    (item.patientTemplateItem.patientTemplate.clinicalStatus && 
                    item.patientTemplateItem.patientTemplate.clinicalStatus.name && 
                    (item.patientTemplateItem.patientTemplate.clinicalStatus.name === "Prior Auth Obtained" || 
                    item.patientTemplateItem.patientTemplate.clinicalStatus.name === "Prior Auth Required" || 
                    item.patientTemplateItem.patientTemplate.clinicalStatus.name === "Prior Auth Pending" || 
                    item.patientTemplateItem.patientTemplate.clinicalStatus.name === "Covered" || 
                    item.patientTemplateItem.patientTemplate.clinicalStatus.name === "Does Not Meet Insurance Criteria"))),
                    ssQty: item.patientTemplateItem.sendSeparatelyQty
                }
                );
            }))
        });

        this.oldQuantity = response.data.item.map(x => x.adjustedQty.toString());
        if (this.isNewFlow2) {
            responseData.item = _.sortBy(responseData.item, i => (i.nonEditable ? true : false))
        }

        this.inventoryCalculationCutbackItems = responseData.item;
        this.adjustedQtySum = _.sumBy(this.inventoryCalculationCutbackItems, (i) => parseInt(i.adjustedQty));
        this.previousAdjustedQty = this.adjustedQtySum;
    }

    @action.bound
    editCutbackQuantity(value) {
        this.isManualOverride = false;
        this.adjustedQtySum = value;
    }

    @action.bound
    async updateCutbackQuantity() {
        if (this.modalParams.data.onCutbackItemUpdate) {
            this.modalParams.data.onCutbackItemUpdate(this.facilityInventoryCalculationItem.id, true);
        }
        const originalValue = this.previousAdjustedQty;
        try {
            await this.service.updateCutbackAdjustedQtyNikoFlow({ calculationItemId: this.facilityInventoryCalculationItem.id, adjustedQty: this.adjustedQtySum, skipUpdate: true });
            if (!this.isManualOverride) {
                this.applyAutomaticCutbackCalculation();
            }
        } catch (err) {
            this.adjustedQtySum = originalValue;
            this.rootStore.notificationStore.error(err && err.data && err.data.errors ? err.data.errors : "Error occurred while trying to change Total Cutback Quantity.", null, 10000);
        }
        finally {
            if (this.modalParams.data.onCutbackItemUpdate) {
                this.modalParams.data.onCutbackItemUpdate(this.facilityInventoryCalculationItem.id, false);
            }
        }
    }

    @action.bound
    applyAutomaticCutbackCalculation() {
        let remaining = this.adjustedQtySum;

        const editableItems = this.inventoryCalculationCutbackItems.filter(i => !i.nonEditable);
        const nonEditableItems = this.inventoryCalculationCutbackItems.filter(i => i.nonEditable);

        editableItems.forEach(item => {
            item.adjustedQty = 0;
            item.difference = item.soQty;
        });
        nonEditableItems.forEach(item => {
            item.adjustedQty = 0;
            item.difference = item.soQty;
        });

        editableItems.forEach(item => {
            if (remaining <= 0) return;

            const maxAllowed = Math.max((item.soQty || 0) - (item.ssQty || 0), 0);
            const applied = Math.min(remaining, maxAllowed);

            item.adjustedQty = applied;
            item.difference = item.soQty - applied;

            remaining -= applied;
        });

        this.adjustedQtySum = _.sumBy(this.inventoryCalculationCutbackItems, i => parseInt(i.adjustedQty) || 0);
    }

    @action.bound
    updateCutbackItemQuantity() {
        this.adjustedQtySum = _.sumBy(this.inventoryCalculationCutbackItems, (i) => parseInt(i.adjustedQty));
    }

    @action.bound
    editCutbackItemQuantity(index, value) {
        this.isManualOverride = true; 
        if (this.inventoryCalculationCutbackItems[index].ssQty && (this.inventoryCalculationCutbackItems[index].soQty - value) < this.inventoryCalculationCutbackItems[index].ssQty) {
            this.rootStore.notificationStore.error("An SS has already been applied to this item. The sales order qty cannot be lower than the SS qty.");
            return;
        }

        if (value <= this.inventoryCalculationCutbackItems[index].soQty) {
            this.inventoryCalculationCutbackItems[index].adjustedQty = value;
            this.inventoryCalculationCutbackItems[index].difference = this.inventoryCalculationCutbackItems[index].soQty - this.inventoryCalculationCutbackItems[index].adjustedQty;
        }
    }

    @action.bound
    async saveChanges() {
        if (this.inventoryCalculationCutbackItems.length > 0) {
            this.disabled = true;
            try {
                await this.service.updateCutbackAdjustedQtyNikoFlow({ calculationItemId: this.facilityInventoryCalculationItem.id, adjustedQty: this.adjustedQtySum });
                this.previousAdjustedQty = this.adjustedQtySum;
                if (this.facilityInventoryCalculationItem.salesOrderQty < this.adjustedQtySum) {
                    this.rootStore.notificationStore.error("Total Cutback Quantity can't be higher than the Total Sales Order Quantity.");
                } else {
                    if (this.facilityInventoryCalculationItem.calculatedQuantity < this.adjustedQtySum) {
                        this.statusConfirmModal.open({message:"You are cutting back on more than what the system calculated. Are you sure you want to proceed?"});
                    }
                    else {
                        await this.service.batchUpdate(this.inventoryCalculationCutbackItems);
                        this.rootStore.notificationStore.success('LIST_LAYOUT.SUCCESS_BATCH_UPDATE');
                        this.modalParams.close();
                    }
                }
                this.disabled = false;
            }
            catch (err) {
                this.rootStore.notificationStore.error('LIST_LAYOUT.ERROR_BATCH_UPDATE');
            }
        }
    }
}

export default NikoFacilityOrderNewFlowInventoryCalculationCutbackEditModalStore;
